import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Loader2,
  RefreshCw,
  FileText,
  ChevronDown,
  ChevronRight,
  X,
  FileSpreadsheet,
  Search,
} from 'lucide-react'
import { AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { DocumentDetail } from '@/components/chat/DocumentDetail'
import { UploadZone } from '@/components/overview/UploadZone'
import { documentService } from '@/services'
import { useAppStore } from '@/stores/appStore'
import { useUpdatesStream } from '@/hooks/useUpdatesStream'
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback'
import type { Document, DocumentCategory } from '@/types/api'
import type { ExplorerDoc } from '@/components/chat/types'

// ── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<DocumentCategory, string> = {
  fundraising_finance: 'Fundraising & Finance',
  customer_intelligence: 'Customer Intelligence',
  product_engineering: 'Product & Engineering',
  go_to_market: 'Go-to-Market',
  legal_compliance: 'Legal & Compliance',
  team_ops: 'Team & Ops',
  unsorted: 'Unsorted',
}

const CATEGORY_ICONS: Record<DocumentCategory, string> = {
  fundraising_finance: '💰',
  customer_intelligence: '🎯',
  product_engineering: '🛠️',
  go_to_market: '📢',
  legal_compliance: '⚖️',
  team_ops: '👥',
  unsorted: '📁',
}

const CATEGORY_COLORS: Record<DocumentCategory, string> = {
  fundraising_finance: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  customer_intelligence: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  product_engineering: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  go_to_market: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  legal_compliance: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
  team_ops: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
  unsorted: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
}

const FILE_TYPE_ICONS: Record<string, { icon: typeof FileText; color: string }> = {
  pdf: { icon: FileText, color: 'text-red-500' },
  pptx: { icon: FileSpreadsheet, color: 'text-orange-500' },
  text: { icon: FileText, color: 'text-blue-500' },
  audio: { icon: FileText, color: 'text-purple-500' },
  url: { icon: FileText, color: 'text-green-500' },
}

const STATUS_DOT: Record<string, string> = {
  completed: 'bg-accent-success',
  processing: 'bg-accent-amber animate-pulse',
  failed: 'bg-accent-error',
  uploading: 'bg-gray-400 animate-pulse',
  pending: 'bg-gray-400',
}

const DOCS_PER_PAGE = 9
const ALL_CATEGORIES: DocumentCategory[] = [
  'fundraising_finance',
  'customer_intelligence',
  'product_engineering',
  'go_to_market',
  'legal_compliance',
  'team_ops',
  'unsorted',
]

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeDate(dateStr?: string): string {
  if (!dateStr) return 'Unknown'
  const ts = Date.parse(dateStr)
  if (Number.isNaN(ts)) return 'Unknown'

  const diff = Date.now() - ts
  if (diff < 60_000) return 'Just now'
  if (diff < 3_600_000) return `${Math.max(1, Math.floor(diff / 60_000))}m ago`
  if (diff < 86_400_000) return `${Math.max(1, Math.floor(diff / 3_600_000))}h ago`

  const days = Math.floor(diff / 86_400_000)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days}d ago`
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function toExplorerDoc(doc: Document): ExplorerDoc {
  return {
    id: doc.id,
    title: doc.name?.trim() || `${doc.type.toUpperCase()} document`,
    type: doc.type === 'pdf' ? 'pdf' : doc.type === 'audio' ? 'audio' : 'text',
    confidence: doc.extractedData?.categoryConfidence,
    category: doc.extractedData?.category,
    subCategory: doc.extractedData?.subCategory,
    uploadedAt: doc.uploadedAt,
  }
}

function countByStatus(docs: Document[]): Record<string, number> {
  const counts: Record<string, number> = { completed: 0, processing: 0, failed: 0 }
  for (const d of docs) {
    const s = d.status
    if (s === 'completed' || s === 'processing' || s === 'failed') counts[s]++
  }
  return counts
}

// ── Sub-components ───────────────────────────────────────────────────────────

function CategoryPill({
  label,
  count,
  active,
  onClick,
}: {
  label: string
  count: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium
        transition-all duration-200 border
        ${
          active
            ? 'bg-accent/10 border-accent/30 text-accent'
            : 'bg-bg-surface border-border text-text-secondary hover:border-accent/30 hover:text-accent'
        }
      `}
    >
      {label}
      <span
        className={`
          inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold
          ${active ? 'bg-accent/15 text-accent' : 'bg-bg-elevated text-text-tertiary'}
        `}
      >
        {count}
      </span>
    </button>
  )
}

function CategorySection({
  label,
  icon,
  docs,
  colorClass,
  currentPage,
  onPageChange,
  onDocClick,
  collapsed,
  onToggle,
}: {
  label: string
  icon: string
  docs: Document[]
  colorClass: string
  currentPage: number
  onPageChange: () => void
  onDocClick: (doc: Document) => void
  collapsed: boolean
  onToggle: () => void
}) {
  const totalPages = Math.ceil(docs.length / DOCS_PER_PAGE)
  const paginatedDocs = docs.slice(0, currentPage * DOCS_PER_PAGE)
  const hasMore = currentPage < totalPages
  const statusCounts = countByStatus(docs)

  return (
    <div className="rounded-xl border border-border bg-bg-surface overflow-hidden">
      {/* Section header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-bg-surface-hover transition-colors"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">{icon}</span>
          <span className="text-sm font-semibold text-text-primary">{label}</span>
          <span className="text-xs text-text-tertiary">({docs.length} docs)</span>
        </div>
        <div className="flex items-center gap-3">
          {/* Status summary */}
          <div className="hidden sm:flex items-center gap-2 text-xs">
            {statusCounts.completed > 0 && (
              <span className="flex items-center gap-1 text-accent-success">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-success" />
                {statusCounts.completed}
              </span>
            )}
            {statusCounts.processing > 0 && (
              <span className="flex items-center gap-1 text-accent-amber">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-amber" />
                {statusCounts.processing}
              </span>
            )}
            {statusCounts.failed > 0 && (
              <span className="flex items-center gap-1 text-accent-error">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-error" />
                {statusCounts.failed}
              </span>
            )}
          </div>
          {collapsed ? (
            <ChevronRight className="h-4 w-4 text-text-tertiary" />
          ) : (
            <ChevronDown className="h-4 w-4 text-text-tertiary" />
          )}
        </div>
      </button>

      {/* Section body */}
      {!collapsed && (
        <>
          {/* Card grid */}
          <div className="px-4 pb-3">
            {paginatedDocs.length === 0 ? (
              <div className="py-8 text-center text-sm text-text-tertiary">
                No documents in this category
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {paginatedDocs.map((doc) => {
                  const status = doc.status ?? 'pending'
                  const FileTypeIconComp =
                    FILE_TYPE_ICONS[doc.type]?.icon || FileText
                  const fileColor = FILE_TYPE_ICONS[doc.type]?.color || 'text-text-tertiary'

                  return (
                    <button
                      key={doc.id}
                      type="button"
                      onClick={() => onDocClick(doc)}
                      className={`
                        group relative flex items-start gap-3 p-3 rounded-lg border
                        bg-bg-elevated border-border
                        hover:border-accent/30 hover:bg-bg-surface-hover
                        transition-all duration-200 text-left
                      `}
                    >
                      {/* Status dot */}
                      <span
                        className={`
                          absolute top-3 right-3 h-2 w-2 rounded-full
                          ${STATUS_DOT[status] || 'bg-gray-400'}
                        `}
                      />

                      {/* File icon */}
                      <div
                        className={`
                          flex h-9 w-9 shrink-0 items-center justify-center rounded-lg
                          bg-bg-surface border border-border
                          group-hover:border-accent/20 transition-colors
                        `}
                      >
                        <FileTypeIconComp className={`h-4 w-4 ${fileColor}`} />
                      </div>

                      {/* Info */}
                      <div className="min-w-0 flex-1 pr-4">
                        <p className="truncate text-sm font-medium text-text-primary">
                          {doc.name?.trim() || `${doc.type.toUpperCase()} document`}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] text-text-tertiary">
                            {formatRelativeDate(doc.uploadedAt ?? doc.createdAt)}
                          </span>
                          {doc.extractedData?.subCategory && (
                            <span className="text-[10px] text-text-tertiary">
                              · {doc.extractedData.subCategory}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Category badge */}
                      <span
                        className={`
                          shrink-0 rounded-md px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider
                          ${colorClass}
                        `}
                      >
                        {doc.type.toUpperCase()}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {/* Show more button */}
          {hasMore && (
            <div className="px-4 pb-3">
              <button
                type="button"
                onClick={() => onPageChange()}
                className="w-full py-2 text-xs font-medium text-text-secondary hover:text-accent border border-dashed border-border rounded-lg hover:border-accent/30 hover:bg-accent/5 transition-all"
              >
                Show {Math.min(DOCS_PER_PAGE, docs.length - currentPage * DOCS_PER_PAGE)} more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

/** Document-only MIME allowlist (no audio/video). */
const DOCUMENT_MIME = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'text/plain',
  'text/markdown',
].join(',')

export function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [selectedDoc, setSelectedDoc] = useState<ExplorerDoc | null>(null)
  const [activeFilter, setActiveFilter] = useState<DocumentCategory | 'all'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [collapsedCategories, setCollapsedCategories] = useState<Set<DocumentCategory>>(new Set())
  const [categoryPages, setCategoryPages] = useState<Record<DocumentCategory, number>>(() =>
    Object.fromEntries(ALL_CATEGORIES.map((c) => [c, 1])) as Record<DocumentCategory, number>
  )
  const startupId = useAppStore((s) => s.startup?.id)

  /**
   * `silent` refreshes leave the current list on screen. Background updates
   * arrive while the user is reading the page, and toggling the loading state
   * on each one would blank it several times during a single ingestion.
   */
  const loadDocuments = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!startupId) {
        setDocuments([])
        setLoading(false)
        return
      }
      if (!opts?.silent) setLoading(true)
      setError(null)
      try {
        const res = await documentService.list(startupId)
        setDocuments(Array.isArray(res?.documents) ? res.documents : [])
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load documents.')
      } finally {
        if (!opts?.silent) setLoading(false)
      }
    },
    [startupId]
  )

  useEffect(() => {
    loadDocuments()
  }, [loadDocuments])

  /**
   * Processing runs server-side for ~40s after upload, so the list fetched at
   * upload time shows `processing` and its status dot pulses. Without this
   * subscription nothing ever told the page the document finished, and the dot
   * pulsed until a manual reload. Every other product screen already subscribes.
   *
   * Debounced because ingestion emits several events in quick succession.
   */
  const debouncedRefresh = useDebouncedCallback(() => {
    void loadDocuments({ silent: true })
  }, 250)

  useUpdatesStream({
    startupId,
    filters: { entityTypes: ['document'] },
    enabled: !!startupId,
    onUpdate: debouncedRefresh,
  })

  // ── Derived data ─────────────────────────────────────────────────────────

  const sorted = useMemo(
    () =>
      [...documents].sort((a, b) => {
        const ta = Date.parse(a.uploadedAt ?? a.createdAt ?? '') || 0
        const tb = Date.parse(b.uploadedAt ?? b.createdAt ?? '') || 0
        return tb - ta
      }),
    [documents]
  )

  const filteredBySearch = useMemo(() => {
    if (!searchQuery.trim()) return sorted
    const q = searchQuery.toLowerCase()
    return sorted.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) ||
        d.extractedData?.category?.toLowerCase().includes(q) ||
        d.extractedData?.subCategory?.toLowerCase().includes(q)
    )
  }, [sorted, searchQuery])

  const groupedByCategory = useMemo(() => {
    const groups: Record<DocumentCategory, Document[]> = {} as Record<DocumentCategory, Document[]>
    for (const c of ALL_CATEGORIES) groups[c] = []
    for (const d of filteredBySearch) {
      const cat = d.extractedData?.category ?? 'unsorted'
      groups[cat as DocumentCategory].push(d)
    }
    return groups
  }, [filteredBySearch])

  const totalDocs = filteredBySearch.length

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (!startupId) return
      setUploadError(null)
      try {
        await documentService.upload(startupId, files)
        void loadDocuments()
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : 'Upload failed. Try again.')
      }
    },
    [startupId, loadDocuments]
  )

  const handleDocClick = useCallback((doc: Document) => {
    setSelectedDoc(toExplorerDoc(doc))
  }, [])

  const toggleCategory = useCallback((cat: DocumentCategory) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }, [])

  const incrementCategoryPage = useCallback((cat: DocumentCategory) => {
    setCategoryPages((prev) => ({ ...prev, [cat]: (prev[cat] ?? 1) + 1 }))
  }, [])

  const resetCategoryPages = useCallback(() => {
    setCategoryPages(Object.fromEntries(ALL_CATEGORIES.map((c) => [c, 1])) as Record<DocumentCategory, number>)
  }, [])

  const handleFilterChange = useCallback((filter: DocumentCategory | 'all') => {
    setActiveFilter(filter)
    resetCategoryPages()
  }, [resetCategoryPages])

  const handleSearchChange = useCallback((q: string) => {
    setSearchQuery(q)
    resetCategoryPages()
  }, [resetCategoryPages])

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="relative flex h-full flex-col">
      {/* Document Detail overlay */}
      <AnimatePresence>
        {selectedDoc && startupId && (
          <div className="absolute inset-0 z-50 flex">
            <div
              className="flex-1 bg-black/50 backdrop-blur-sm"
              onClick={() => setSelectedDoc(null)}
            />
            <DocumentDetail doc={selectedDoc} onClose={() => setSelectedDoc(null)} startupId={startupId} />
          </div>
        )}
      </AnimatePresence>

      <div className="mx-auto w-full max-w-6xl p-4 lg:p-6">
        {/* Header */}
        <div className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl font-bold text-text-primary">Documents</h1>
            <p className="mt-1 text-sm text-text-secondary">
              {totalDocs} document{totalDocs !== 1 ? 's' : ''} across {ALL_CATEGORIES.filter((c) => (groupedByCategory[c]?.length ?? 0) > 0).length} categories
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void loadDocuments()}
              disabled={loading}
              className="gap-1.5"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Search + Filter bar */}
        {totalDocs > 0 && (
          <div className="mb-4 space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
              <input
                type="text"
                placeholder="Search documents by name, category, or type..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-bg-surface border border-border rounded-lg placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-accent/30 focus:border-accent/30"
              />
              {searchQuery && (
                <button
                  type="button"
                  title="Clear search"
                  onClick={() => handleSearchChange('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-primary"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Category pills */}
            <div className="flex flex-wrap items-center gap-2">
              <CategoryPill
                label="All"
                count={totalDocs}
                active={activeFilter === 'all'}
                onClick={() => handleFilterChange('all')}
              />
              {ALL_CATEGORIES.map((cat) => {
                const count = groupedByCategory[cat]?.length ?? 0
                if (count === 0) return null
                return (
                  <CategoryPill
                    key={cat}
                    label={CATEGORY_LABELS[cat]}
                    count={count}
                    active={activeFilter === cat}
                    onClick={() => handleFilterChange(cat)}
                  />
                )
              })}
            </div>
          </div>
        )}

        {/* Upload zone */}
        <UploadZone
          onUpload={handleUpload}
          onRejected={(reason) => setUploadError(reason)}
          acceptedMime={DOCUMENT_MIME}
          compact={totalDocs > 0}
        />
        {uploadError && (
          <div role="alert" className="mt-3 text-sm px-3 py-2 rounded-md border border-error/30 bg-error/10 text-text-primary">
            {uploadError}
          </div>
        )}

        {/* Content */}
        {loading && totalDocs === 0 ? (
          <div className="flex items-center justify-center py-16 text-text-secondary">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading documents...
          </div>
        ) : error ? (
          <div className="rounded-xl border border-accent-error/30 bg-accent-error/5 p-6 text-center">
            <p className="text-sm text-accent-error">{error}</p>
            <button onClick={() => void loadDocuments()} className="mt-2 text-xs text-accent-amber hover:underline">
              Retry
            </button>
          </div>
        ) : totalDocs === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
            <FileText className="mb-3 h-8 w-8 text-text-tertiary" />
            <p className="text-sm font-medium text-text-primary">No documents yet</p>
            <p className="mt-1 text-xs text-text-secondary">
              Upload a document to start building your knowledge base.
            </p>
          </div>
        ) : activeFilter !== 'all' && (groupedByCategory[activeFilter]?.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-12 text-center">
            <p className="text-sm text-text-secondary">No documents in this category</p>
            <button
              onClick={() => handleFilterChange('all')}
              className="mt-2 text-xs text-accent hover:underline"
            >
              View all documents
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {activeFilter === 'all'
              ? ALL_CATEGORIES.map((cat) => {
                  const catDocs = groupedByCategory[cat] ?? []
                  if (catDocs.length === 0) return null
                  return (
                    <CategorySection
                      key={cat}
                      label={CATEGORY_LABELS[cat]}
                      icon={CATEGORY_ICONS[cat]}
                      docs={catDocs}
                      colorClass={CATEGORY_COLORS[cat]}
                      currentPage={categoryPages[cat] ?? 1}
                      onPageChange={() => incrementCategoryPage(cat)}
                      onDocClick={handleDocClick}
                      collapsed={collapsedCategories.has(cat)}
                      onToggle={() => toggleCategory(cat)}
                    />
                  )
                })
              : (() => {
                  const cat = activeFilter
                  const catDocs = groupedByCategory[cat] ?? []
                  return catDocs.length > 0 ? (
                    <CategorySection
                      key={cat}
                      label={CATEGORY_LABELS[cat]}
                      icon={CATEGORY_ICONS[cat]}
                      docs={catDocs}
                      colorClass={CATEGORY_COLORS[cat]}
                      currentPage={categoryPages[cat] ?? 1}
                      onPageChange={() => incrementCategoryPage(cat)}
                      onDocClick={handleDocClick}
                      collapsed={collapsedCategories.has(cat)}
                      onToggle={() => toggleCategory(cat)}
                    />
                  ) : null
                })()}
          </div>
        )}
      </div>
    </div>
  )
}
